/**
 * In-app notification sound — local preferences (mirrors Command Center defaults).
 * enabled: true, volume: 0.5
 */

const STORAGE_KEY = 'taskopsInAppNotificationSound';

const DEFAULTS = {
  enabled: true,
  volume: 0.5,
};

let audioCtx = null;

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return {
      enabled: parsed.enabled !== false,
      volume: typeof parsed.volume === 'number' ? parsed.volume : DEFAULTS.volume,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function savePrefs(prefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  return audioCtx;
}

/** Call once after a user gesture to satisfy browser autoplay policies. */
export async function unlockNotificationAudio() {
  const ctx = getAudioContext();
  if (!ctx) return false;
  try {
    if (ctx.state === 'suspended') await ctx.resume();
    return true;
  } catch {
    return false;
  }
}

export function getNotificationSoundPrefs() {
  return loadPrefs();
}

export function setNotificationSoundEnabled(enabled) {
  const next = { ...loadPrefs(), enabled: Boolean(enabled) };
  savePrefs(next);
  return next;
}

export function setNotificationSoundVolume(volume) {
  const v = Math.max(0, Math.min(1, Number(volume)));
  const next = { ...loadPrefs(), volume: v };
  savePrefs(next);
  return next;
}

/** Short two-tone chime — no external asset required. */
export function playInAppNotificationSound({ force = false, volume = null } = {}) {
  const prefs = loadPrefs();
  if (!prefs.enabled && !force) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  const vol = volume != null ? volume : prefs.volume;
  const peak = Math.max(0.04, Math.min(0.5, vol * 0.4));

  const playTone = (freq, start, duration) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(peak, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + duration + 0.02);
  };

  try {
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const t = ctx.currentTime;
    playTone(880, t, 0.12);
    playTone(1174, t + 0.1, 0.18);
  } catch {
    // Autoplay blocked until user interacts — expected on first load.
  }
}

export function testNotificationSound() {
  playInAppNotificationSound({ force: true });
}
